import { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowCounterClockwiseIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import Typhography from "../../shared/Typhography/Typhography";
import LoadingPage from "../../shared/LoadingPage/LoadingPage";
import { useEndpoints } from "../../hooks/useEndpoints";
import { USER_TYPE_OPTIONS } from "../../constants/config";
import { getLabelFromOptions } from "../../lib/DurationPicker/utils";
import { formatFullName } from "../../utils/functions";
import style from "./UserDetailPage.module.scss";
import PlayerDetail from "../../leopart-update/pages/PlayerDetail";
import { DataProvider } from "../../leopart-update/context/DataContext";

// ─── CaretLeft icon ───────────────────────────────────────────────────────────

function CaretLeft() {
  return (
    <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
      <path
        d="M10 12L6 8L10 4"
        stroke="#00212C"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const UserDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const endpoints = useEndpoints();

  const query = useQuery({
    queryKey: ["user-details", id],
    queryFn: async () => {
      const { data } = await endpoints.getUserDetails(id!);
      return data;
    },
    enabled: !!id,
  });

  const raw = query.data;
  const user = raw?.user;
  const fullName = user ? formatFullName(user.firstName, user.lastName) : "—";
  const isLoading = query.isPending;
  const userTypeLabel = user?.type
    ? getLabelFromOptions(USER_TYPE_OPTIONS, user.type) || user.type
    : null;

  const avatarUrl = user?.image
    ? `${process.env.REACT_APP_BASE_URL_IMAGE}${user.image.image ?? user.image}`
    : undefined;

  const teamName = (user as any)?.teams?.[0]?.name ?? undefined;

  if (isLoading && !raw) return <LoadingPage />;

  if (query.isError)
    return (
      <div className={style.pageWrapper}>
        <div className={style.scrollArea}>
          <div className={style.inner}>
            <button className={style.backLink} onClick={() => navigate(-1)}>
              <CaretLeft />
              <span className={style.backLinkText}>Indietro</span>
            </button>
            <div className={style.errorState}>
              <WarningCircleIcon className={style.errorIcon} />
              <Typhography size="bold-16">
                Impossibile caricare i dati
              </Typhography>
              <Typhography size="regular-14" className={style.errorSubtext}>
                Si è verificato un errore durante il caricamento del profilo
                utente.
              </Typhography>
              <button
                className={style.retryButton}
                onClick={() => query.refetch()}
              >
                <ArrowCounterClockwiseIcon size={14} />
                Riprova
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <DataProvider>
      <PlayerDetail
        playerId={id!}
        playerName={fullName}
        playerPosition={userTypeLabel ?? undefined}
        playerTeam={teamName}
        avatarSrc={avatarUrl}
      />
    </DataProvider>
  );
};

export default UserDetailPage;
